import {Module} from '@fantasmagames/slot/src/common/Module';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {AutoplayHandlerEvents} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {BetButtonEvents} from '@fantasmagames/slot/src/modules/keypad/bet/BetButtonEvents';
import {BetButtonView} from '@fantasmagames/slot/src/modules/keypad/bet/BetButtonView';
import {BetButtonViewEvents} from '@fantasmagames/slot/src/modules/keypad/bet/BetButtonViewEvents';
import {StopAutoplayButtonEvents} from '@fantasmagames/slot/src/modules/keypad/stopautoplaybutton/StopAutoplayButtonEvents';
import {InfoPopupEvents} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupEvents';
import {InfoPopupTypes} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupTypes';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {StartScreenEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenEvents';
import {BetButtonBasicState} from '@fantasmagames/slot/src/modules/keypad/bet/states/BetButtonBasicState';
import {BetButtonWhileSpinningState} from '@fantasmagames/slot/src/modules/keypad/bet/states/BetButtonWhileSpinningState';

export class BetButtonModule extends Module<BetButtonView> {
	public static readonly BET_CLICKED_STATE: string = 'betClicked';

	public static readonly BASIC: string = 'basic';
	public static readonly WHILESPINNING: string = 'whilespinning';

	protected _autoPlayActive: boolean = false;
	protected _isFreeSpins: boolean = false;
	protected _startscreenExitFinished: boolean = false;

	public afterInit(): void {
		super.afterInit();

		this.hideBetButton();
	}

	public restore(): void {
		super.restore();

		if (this.logic.isFreeSpinsStarted) {
			this._isFreeSpins = true;
			this.hideBetButton();
		}
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.BET_VIEW_MOBILE);
		} else {
			this.setView(Views.BET_VIEW);
		}
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(AutoplayHandlerEvents.START_AUTOPLAY_SPIN, this.onStartAutoplaySpin);

		this.onCurrentStateHandle(StopAutoplayButtonEvents.STOP_AUTOPLAY_BUTTON_CLICKED);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsFinished);

		this.on(FlowModuleEvents.FREESPINS_WIN, this.onFreeSpinsWin);
		this.on(FlowModuleEvents.BONUS_GAME_START, this.onBonusGameStarted);
		this.on(FlowModuleEvents.BONUS_GAME_RESTORED, this.onBonusGameRestored);
		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted);

		this.on(InfoPopupEvents.INFO_POPUP_CLOSED, this.onInfoPopupOkClicked);

		this.on(StartScreenEvents.EXIT_FINISHED, this.onStartScreenExitFinished);

		this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);

		this.onViewEmitEvent(BetButtonViewEvents.BET_BUTTON_CLICKED, BetButtonEvents.BET_BUTTON_CLICKED);
	}

	protected setupStates(): void {
		this.addState(new BetButtonBasicState(this, BetButtonModule.BASIC));
		this.addState(new BetButtonWhileSpinningState(this, BetButtonModule.WHILESPINNING));
	}

	protected enterFirstState(): void {
		this.gotoState(BetButtonModule.BASIC);
	}

	protected onStartScreenExitFinished(): void {
		this._startscreenExitFinished = true;
		this.showBetButton();
	}

	protected onInfoPopupOkClicked(eventId: string, eventType: InfoPopupTypes): void {
		if (eventType === InfoPopupTypes.RESTORE || eventType === InfoPopupTypes.NO_MONEY) {
			this.showBetButton();
		}

		if (eventType === InfoPopupTypes.RESTORE) {
			this.hideBetButton();
		}
	}

	protected onFreeSpinsStarted(): void {
		this._isFreeSpins = true;
		this.hideBetButton();
	}

	protected onFreeSpinsFinished(): void {
		this._isFreeSpins = false;

		if (!this.autoPlayActive) {
			this.showBetButton();
		}
		this.enableBetButton();
	}

	protected onFreeSpinsWin(): void {
		this._isFreeSpins = true;
		this.hideBetButton();
	}

	protected onBonusGameStarted(): void {
		this.hideBetButton();
		this.disableBetButton();
	}

	protected onBonusGameRestored(): void {
		this.hideBetButton();
		this.disableBetButton();
	}

	protected onStartAutoplaySpin(): void {
		if (this.logic.isEnoughMoneyForSpin()) {
			this.autoPlayActive = true;
			this.hideBetButton();
		} else {
			this.autoPlayActive = false;
			this.showBetButton();
		}
	}

	protected onCanSpin(): void {
		this.gotoState(BetButtonModule.BASIC);
	}

	protected onReelsSpinningStarted(): void {
		this.gotoState(BetButtonModule.WHILESPINNING);
	}

	public hideBetButton(): void {
		this.view.hideBetButton();
	}

	public showBetButton(): void {
		if (!this._isFreeSpins) {
			this.view.showBetButton();
		}
	}

	public enableBetButton(): void {
		this.view.enableBetButton();
	}

	public disableBetButton(): void {
		this.view.disableBetButton();
	}

	public showView(): void {
		if (!this._isFreeSpins) {
			this.view.show();
			this.showBetButton();
		}
	}

	protected hideView(): void {
		this.view.hide();
		this.hideBetButton();
	}

	public set autoPlayActive(value: boolean) {
		this._autoPlayActive = value;
	}

	public get autoPlayActive(): boolean {
		return this._autoPlayActive;
	}

	public get startscreenExitFinished(): boolean {
		return this._startscreenExitFinished;
	}

	public get isFreeSpins(): boolean {
		return this._isFreeSpins;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/keypad/bet/BetButtonModule.ts